import styled from '@emotion/styled'
import CenterModal from 'shop/components/Modal/CenterModal'
import { useConsumerCart, useCheckout } from 'shop/hooks'
import { Button, Heading } from 'shop/components'
import { TrackableEvent } from 'tracker'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { slideUp } from '../Shop/commonStyles'
import {
  ConsumerCart,
  InvalidTimeslotPrepTimeWarning,
  InvalidTimeslotWarning
} from 'shop/types/cart'
import { PREORDER_ORDER } from 'shop/types'
import { formatMoney, returnToShop } from 'shop/utils/common'
import ErrorModal from '../Modal/ErrorModal'
import { trackTimeslotEvent } from './utils'

type TimeSlotModalProps = {
  isStoreOpen: boolean
  navigate: NavigateFunction
}

const handleReturnToShop = (
  cart: ConsumerCart | null,
  navigate: NavigateFunction
): void => {
  trackTimeslotEvent(TrackableEvent.ReturnFromTimeslot)
  returnToShop(cart?.store.slug, navigate)
}

/** Discriminates between timeslot validation warning types (InvalidTimeslotWarning | InvalidTimeslotPrepTimeWarning) & renders the correct modal */
export const TimeSlotModalWrapper = ({
  timeSlotValidation
}: {
  timeSlotValidation: InvalidTimeslotWarning | InvalidTimeslotPrepTimeWarning
}): JSX.Element => {
  const { cart, getConsumerCart } = useConsumerCart()
  const { setDynamicPrepTimeSlotValidation } = useCheckout()
  const navigate = useNavigate()

  if ('nextAvailableTimeslot' in timeSlotValidation) {
    const { isStoreOpen } = timeSlotValidation
    return <TimeSlotModal isStoreOpen={isStoreOpen} navigate={navigate} />
  } else {
    const primaryButton = {
      message: 'Update your cart',
      onClick: () => {
        // Call getConsumerCart to ensure cart total is up to date on shop page in case of multiple tab usage
        getConsumerCart().finally(() => {
          handleReturnToShop(cart, navigate)
          // Clear validation state after user has been redirected
          setDynamicPrepTimeSlotValidation(null)
        })
      },
      icon: null
    }
    const { restriction } = timeSlotValidation
    return (
      <ErrorModal
        errorCode={'INVALID_TIMESLOT_DYNAMIC_PREPARATION_TIME'}
        primaryButton={primaryButton}
        errorMessages={[
          restriction.maxPossibleBasketValue
            ? `Your order is too big to prepare before we close. Try reducing it below ${formatMoney(restriction.maxPossibleBasketValue)}`
            : ''
        ]}
        errorTitle={'Too late for large orders'}
      />
    )
  }
}

const TimeSlotModal = ({ isStoreOpen, navigate }: TimeSlotModalProps) => {
  const { cart, clearConsumerCart } = useConsumerCart()
  const isPreorder = cart?.fulfillment.orderType === PREORDER_ORDER

  const renderMessage = (): JSX.Element => {
    const testId = 'expired-indefinitely'
    const storeOpenMsg = isStoreOpen
      ? 'Please return and order for another day.'
      : 'Please return and order for a day we are open.'
    const message = isPreorder
      ? `Unfortunately the timeslot you selected has expired and there are no timeslots available on your selected date. ${storeOpenMsg}`
      : `Unfortunately the timeslot you selected has expired and there are no timeslots available today. ${storeOpenMsg}`

    return <p data-testid={testId}>{message}</p>
  }

  return (
    <CenterModal canClose={false}>
      <Prompt data-testid='timeslot-modal'>
        <Heading as='h2' fontWeight={600}>
          Timeslot expired
        </Heading>
        {renderMessage()}
        <Actions>
          <Button
            variant={'secondary'}
            onClick={() => {
              clearConsumerCart()
              handleReturnToShop(cart, navigate)
            }}
            testId='back-to-shop'
          >
            Return
          </Button>
        </Actions>
      </Prompt>
    </CenterModal>
  )
}

const Actions = styled.div(() => ({
  marginTop: '16px',
  width: '100%'
}))

const Prompt = styled.div(({ theme }) => ({
  animation: `${slideUp} 100ms ease-out`,
  position: 'relative',
  borderRadius: '4px',
  overflowY: 'auto',
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  margin: '24px auto',
  maxWidth: '480px',
  width: '100%',
  padding: '16px',
  alignItems: 'center',
  '> p': {
    marginBottom: '16px'
  },
  [theme.mediaQueries.viewport7]: {
    maxWidth: '960px',
    width: '100%',
    height: 'auto',
    padding: '32px',
    marginBottom: '32px'
  }
}))

export default TimeSlotModal
